import { useEffect } from 'react';
import { callCenterApi } from './api';
import { useCallCenter } from './store';

/**
 * Load the call-centre bootstrap once after sign-in.
 *
 * Called unconditionally from the app shell, for EVERY signed-in user, and that
 * is deliberate: the answer is what decides whether this till shows a Pending
 * Orders tab at all, so it cannot be gated on the thing it is being asked to
 * determine. `/bootstrap` is the one route in the module that an ordinary
 * cashier may call, and it answers `provisioned: false` rather than erroring on
 * a tenant that never ran migration 046 — so on the overwhelming majority of
 * tills this is one small GET at sign-in and nothing else ever happens.
 *
 * Failures are swallowed. A tenant without the module, a tenant with it
 * switched off, and a momentary network blip all produce the same outcome —
 * `bootstrap` stays null and every call-centre surface stays hidden — which is
 * exactly the behaviour that keeps this feature from being able to break a till
 * that does not use it.
 */
export function useCallCenterBootstrap(enabled: boolean): void {
  const setBootstrap = useCallCenter((s) => s.setBootstrap);
  const setLoading = useCallCenter((s) => s.setLoadingBootstrap);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    void callCenterApi
      .bootstrap()
      .then((data) => {
        if (alive) setBootstrap(data);
      })
      .catch(() => {
        /* see the note above — silence is the correct failure mode here */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [enabled, setBootstrap, setLoading]);
}
